import apiClient from './config/axios';

/**
 * Navigation API module
 * Handles all navigation-related API calls (header, mega menu, etc.)
 */
export const navigationApi = {
    /**
     * Get header links (main navigation categories)
     * @returns {Promise<{success: boolean, data?: array, message?: string}>}
     */
    getHeaderLinks: async () => {
        try {
            const response = await apiClient.get('/api/header-links');
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch header links',
            };
        }
    },

    /**
     * Get all main categories
     * @returns {Promise<{success: boolean, data?: array, message?: string}>}
     */
    getMainCategories: async () => {
        try {
            const response = await apiClient.get('/api/main-categories');
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch main categories',
            };
        }
    },

    /**
     * Get all brands
     * @returns {Promise<{success: boolean, data?: array, message?: string}>}
     */
    getBrands: async () => {
        try {
            const response = await apiClient.get('/api/brands');
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch brands',
            };
        }
    },

    /**
     * Get mega menu data for a specific category
     * @param {string} categorySlug - Category slug
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getCategoryMegaMenu: async (categorySlug) => {
        try {
            const response = await apiClient.get(`/api/category-mega-menu/${categorySlug}`);
            return response.data;
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch mega menu',
            };
        }
    },

    /**
     * Get navigation data (header links, categories, brands) in one call
     * @returns {Promise<{success: boolean, data?: object, message?: string}>}
     */
    getNavigationData: async () => {
        try {
            const [linksResponse, categoriesResponse, brandsResponse] = await Promise.all([
                apiClient.get('/api/header-links'),
                apiClient.get('/api/main-categories'),
                apiClient.get('/api/brands'),
            ]);

            return {
                success: true,
                data: {
                    headerLinks: linksResponse.data.success ? linksResponse.data.data : [],
                    categories: categoriesResponse.data.success ? categoriesResponse.data.data : [],
                    brands: brandsResponse.data.success ? brandsResponse.data.data : [],
                },
            };
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch navigation data',
            };
        }
    },
};

export default navigationApi;
